import { useEffect, useState } from 'react';

import { Shell } from '../componentes/Shell.jsx';
import { guardarConConexion } from '../nucleo/api/cliente.js';
import { cambiarContrasenaPropia, consultarMonedas, guardarPreferenciasPropias } from '../nucleo/api/nucleoApi.js';
import { obtenerDesplazamientoLegible, obtenerZonaDetectada } from '../nucleo/fecha/fechaLocal.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';

/**
 * Mi perfil (RF-026).
 *
 * Lo que NO está aquí, a propósito: selector de zona horaria (D-06), SELECTOR DE
 * TEMA —el tema es del dispositivo y vive en Ajustes › Apariencia (D-08)—,
 * permisos o roles (no existen, D-05) y preferencias de otras personas
 * (PREFERENCIA_PERSONAL_AJENA, RN-038).
 */
export default function Perfil() {
  const { sesion, establecerSesion } = useSesion();
  const [monedas, setMonedas] = useState([]);
  const [idMoneda, setIdMoneda] = useState(sesion?.idMonedaPredeterminada ?? 0);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  const [contrasenas, setContrasenas] = useState({ anterior: '', nueva: '', repetida: '' });
  const zona = obtenerZonaDetectada();

  useEffect(() => { consultarMonedas(true).then(setMonedas).catch(setError); }, []);

  async function guardarPreferencias() {
    setError(null); setAviso(null);
    try {
      const actualizado = await guardarConConexion(() => guardarPreferenciasPropias(Number(idMoneda)));
      establecerSesion(actualizado);
      setAviso('Preferencias guardadas.');
    } catch (fallo) {
      setError(fallo);
    }
  }

  async function cambiarContrasena() {
    setError(null); setAviso(null);

    if (contrasenas.nueva.length < 8) {
      return setError({ mensaje: 'La contraseña debe tener al menos 8 caracteres.' });
    }
    if (contrasenas.nueva !== contrasenas.repetida) {
      return setError({ mensaje: 'Las contraseñas no coinciden.' });
    }

    try {
      await guardarConConexion(() => cambiarContrasenaPropia(contrasenas.anterior, contrasenas.nueva));
      setContrasenas({ anterior: '', nueva: '', repetida: '' });
      return setAviso('Contraseña cambiada.');
    } catch (fallo) {
      return setError(fallo);
    }
  }

  return (
    <Shell titulo="Mi perfil" volverA="/ajustes">
      {sesion?.debeCambiarContrasena && (
        <div className="aviso w">
          <b>▲ Debes cambiar tu contraseña.</b> La otra persona del hogar te la restableció; para
          seguir usando la aplicación, define una nueva.
        </div>
      )}

      <AvisoDeError error={error} />
      {aviso && <div className="aviso n">✔ {aviso}</div>}

      <div className="card">
        <h3>Mis datos</h3>
        <p className="ayuda">
          Estos son los tuyos. Los de la otra persona usuaria se ven en Ajustes › Personas del hogar.
        </p>

        <label htmlFor="nombre">Nombre para mostrar</label>
        <input id="nombre" value={sesion?.nombre ?? ''} readOnly />
        {/* [RENOMBRADO 2026-10-10 · incremento 16/17 · RN-647, D-347] */}
        <div className="ayuda">Cualquiera de las dos puede editar el nombre desde Personas del hogar.</div>

        <label htmlFor="clave">Clave de acceso</label>
        <input id="clave" value={sesion?.claveUsuario ?? ''} readOnly />
        <div className="ayuda">No se modifica en esta versión.</div>
      </div>

      <div className="card">
        <h3>Mis preferencias</h3>
        <p className="ayuda">Solo tú puedes cambiarlas, y solo para ti.</p>

        <label htmlFor="moneda">Moneda predeterminada de captura</label>
        <select id="moneda" value={idMoneda} onChange={(e) => setIdMoneda(e.target.value)}>
          {monedas.map((moneda) => (
            <option key={moneda.idMoneda} value={moneda.idMoneda}>
              {moneda.codigoISO} · {moneda.nombre}
            </option>
          ))}
        </select>
        <div className="ayuda">
          Es la moneda que se propone al crear una cuenta o capturar. No implica conversión: MXN y
          USD nunca se suman.
        </div>

        <button className="btn ancho" onClick={guardarPreferencias}>Guardar preferencias</button>

        <div className="info">
          <b>Zona detectada del dispositivo:</b> {zona ?? 'no disponible'} ({obtenerDesplazamientoLegible()})<br />
          Solo lectura. <b>No es una preferencia y no se guarda</b>: se detecta cada vez del aparato
          con el que estás usando la aplicación. Si viajas, tus movimientos ya guardados <b>no se
          recalculan nunca</b>.
        </div>
      </div>

      <div className="card">
        <h3>Cambiar mi contraseña</h3>
        {/* Fila 32 del inventario (RN-627a, D-333): cae el código «(S-12)»; el resto
            del texto ya era conforme y no se toca (RN-629). */}
        <p className="ayuda" data-prueba="perfil-contrasena-ayuda">
          Necesitas la contraseña actual. ¿La olvidaste? Que la restablezca la otra persona
          del hogar.
        </p>

        <label htmlFor="anterior">Contraseña actual</label>
        <input id="anterior" type="password" value={contrasenas.anterior} onChange={(e) => setContrasenas({ ...contrasenas, anterior: e.target.value })} />

        <label htmlFor="nueva">Contraseña nueva</label>
        <input id="nueva" type="password" placeholder="Mínimo 8 caracteres" value={contrasenas.nueva} onChange={(e) => setContrasenas({ ...contrasenas, nueva: e.target.value })} />

        <label htmlFor="repetida">Repetir contraseña nueva</label>
        <input id="repetida" type="password" value={contrasenas.repetida} onChange={(e) => setContrasenas({ ...contrasenas, repetida: e.target.value })} />

        <button className="btn ancho" onClick={cambiarContrasena}>Cambiar contraseña</button>
      </div>

      {/* Fila 10 del inventario del incremento 15 (RF-222, D-325): el catálogo de
          ausencias («Lo que NO está en esta pantalla, a propósito») se sustituyó por lo
          único que de ahí le sirve a la persona. El destino «Ajustes › Apariencia» es el
          rótulo real del grupo de la pantalla Ajustes. */}
      <div className="aviso i" data-prueba="perfil-ayuda">
        El tema se cambia en Ajustes › Apariencia. Desactivar tu propio acceso lo hace la otra
        persona del hogar y corta la sesión al instante.
      </div>
    </Shell>
  );
}
